import * as React from "react";
import moment from "moment";
import { Icon } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

type CustomPickersDayProps = {
  dayIsBetween?: boolean;
  isFirstDay?: boolean | null;
  isLastDay?: boolean | null;
  isHovered?: boolean;
};

const CustomPickersDay = styled(PickersDay as any, {
  shouldForwardProp: (prop) =>
    prop !== "dayIsBetween" &&
    prop !== "isFirstDay" &&
    prop !== "isLastDay" &&
    prop !== "isHovered",
})<CustomPickersDayProps>(
  ({ theme, dayIsBetween, isFirstDay, isLastDay, isHovered }) => ({
    // Remove slow animation
    transition: "none",

    // Selection Styles (Work Week or Full Week)
    ...(dayIsBetween && {
      borderRadius: "initial",
      backgroundColor: (theme.vars || theme).palette.primary.light,
      color: (theme.vars || theme).palette.common.white,
      // Ensure hovering over a selected day doesn't change color locally
      "&:hover, &:focus": {
        backgroundColor: (theme.vars || theme).palette.primary.light,
      },
      // Force specific style for the "selected" day (the one clicked)
      "&.Mui-selected": {
        backgroundColor: (theme.vars || theme).palette.primary.light,
        color: (theme.vars || theme).palette.common.white,
        "&:hover, &:focus": {
          backgroundColor: (theme.vars || theme).palette.primary.light,
        },
      },
    }),

    // UNSELECTED BUT VALUE (e.g. Sunday when highlightWeekDaysOnly is true)
    // We need to forcefully revert the selected styles if dayIsBetween is false.
    // We use !important to override MUI's internal specificity for 'selected' state if it persists.
    ...(!dayIsBetween && {
      "&.Mui-selected": {
        backgroundColor: "transparent !important",
        color: (theme.vars || theme).palette.text.primary,
        "&:hover, &:focus": {
          backgroundColor: "var(--primary-tint)",
        },
      },
      // Revert 'focus' style which causes the 'filled' look on open because the value (Sunday) is focused by default
      "&:focus, &.Mui-focusVisible": {
        backgroundColor: "transparent !important",
        color: (theme.vars || theme).palette.text.primary,
        "&:hover": {
          backgroundColor: "var(--primary-tint)",
        },
      },
    }),

    // Hover Styles (for unselected days)
    ...(!dayIsBetween &&
      isHovered && {
        borderRadius: "initial",
        backgroundColor: "var(--primary-tint)",
        "&:hover, &:focus": {
          backgroundColor: "var(--primary-tint)",
        },
      }),

    // Border Radius for Start/End of Week (Applies to both Selection and Hover)
    ...((dayIsBetween || isHovered) &&
      isFirstDay && {
        borderTopLeftRadius: "var(--radius-round)",
        borderBottomLeftRadius: "var(--radius-round)",
      }),
    ...((dayIsBetween || isHovered) &&
      isLastDay && {
        borderTopRightRadius: "var(--radius-round)",
        borderBottomRightRadius: "var(--radius-round)",
      }),
  })
);

const Day = (props) => {
  const {
    day,
    selectedDay,
    hoveredDay,
    onHover,
    highlightWeekDaysOnly,
    // selected,
    ...other
  } = props;

  // Determine the start and end of the visual range (Week or Work Week)
  // But always relative to the week the day belongs to
  const getRange = (baseDate) => {
    if (!baseDate) return null;
    const startOfWeek = baseDate.clone().startOf("week"); // Sunday

    if (highlightWeekDaysOnly) {
      // Monday to Friday
      return {
        start: startOfWeek.clone().add(1, "days"),
        end: startOfWeek.clone().add(5, "days"),
      };
    }
    // Sunday to Saturday
    return {
      start: startOfWeek,
      end: baseDate.clone().endOf("week"),
    };
  };

  const range = getRange(day);
  const isFirstDay = range && day.isSame(range.start, "day");
  const isLastDay = range && day.isSame(range.end, "day");

  let dayIsBetween = false;
  let isHovered = false;

  if (selectedDay) {
    const { start, end } = getRange(selectedDay);
    dayIsBetween = day.isBetween(start, end, "day", "[]");
  }

  if (hoveredDay) {
    const { start, end } = getRange(hoveredDay);
    isHovered = day.isBetween(start, end, "day", "[]");
  }

  return (
    <CustomPickersDay
      {...other}
      day={day}
      selected={dayIsBetween}
      dayIsBetween={dayIsBetween}
      isFirstDay={isFirstDay}
      isLastDay={isLastDay}
      isHovered={isHovered}
      disableMargin
      onMouseEnter={() => onHover && onHover(day)}
      onMouseLeave={() => onHover && onHover(null)}
    />
  );
};

// Custom TextField Wrapper to force display of range string
const WeekField = (props) => {
  const { displayValue, inputProps, ...other } = props;
  return (
    <TextField
      {...other}
      value={displayValue}
      slotProps={{ htmlInput: { ...inputProps, value: displayValue } }}
    />
  );
};

export default function RpWeekPicker({
  value,
  onChange,
  highlightWeekDaysOnly = false,
  size = "small",
  hideCalendarIcon = false,
  useDefaultCalendarIcon = false,
  enableWeeks = [],
  disabled = false,
  inputAlign = "center",
  ...other
}) {
  const showPickerButton = !hideCalendarIcon;
  const [hoveredDay, setHoveredDay] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  // Safe memoization of the value to ensure it's a valid Moment object or null
  const formattedValue = React.useMemo(() => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m : null;
  }, [value]);

  const handleChange = (newValue) => {
    if (newValue && moment.isMoment(newValue) && newValue.isValid()) {
      const startOfWeek = newValue.clone().startOf("week");
      onChange?.(startOfWeek.format("YYYY-MM-DD"));
      setOpen(false); // Close on selection
    } else {
      onChange?.("");
    }
  };

  const getDisplayValue = () => {
    if (!formattedValue) return "";
    const start = formattedValue.clone().startOf("week");
    const end = formattedValue.clone().endOf("week");
    return `${start.format("MMM DD, YYYY")} - ${end.format("MMM DD, YYYY")}`;
  };

  const enabledWeeksSet = React.useMemo(() => {
    return new Set(enableWeeks || []);
  }, [enableWeeks]);

  const referenceDate = React.useMemo(() => {
    const sortedWeeks = (enableWeeks || [])
      .map((week) => moment(week))
      .filter((week) => week.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());

    if (!sortedWeeks.length) {
      return undefined;
    }

    const firstWeekStart = sortedWeeks[0].clone().startOf("week");

    return highlightWeekDaysOnly
      ? firstWeekStart.add(3, "days")
      : firstWeekStart;
  }, [enableWeeks, highlightWeekDaysOnly]);

  const {
    slots: customSlots,
    slotProps: customSlotProps,
    ...otherProps
  } = other || {};

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
      <DatePicker
        referenceDate={referenceDate}
        value={formattedValue}
        onChange={handleChange}
        open={open}
        onOpen={() => !disabled && setOpen(true)}
        onClose={() => setOpen(false)}
        enableAccessibleFieldDOMStructure={false}
        slots={{
          day: Day,
          textField: WeekField,
          ...(showPickerButton &&
            !useDefaultCalendarIcon && {
              openPickerIcon: () => (
                <Icon component={RpSvgIcon} name="month_calender" />
              ),
            }),
          ...customSlots,
        }}
        slotProps={{
          openPickerButton: showPickerButton
            ? {
                disabled,
                sx: (theme) => ({
                  color: disabled
                    ? (theme.vars || theme).palette.action.disabled
                    : (theme.vars || theme).palette.primary.main,
                  padding: "2px",
                  marginLeft: "-6px",
                  minWidth: "auto",
                }),
              }
            : { sx: { display: "none" } },
          day: {
            selectedDay: formattedValue,
            hoveredDay,
            onHover: setHoveredDay,
            highlightWeekDaysOnly,
            ...customSlotProps?.day,
          },
          textField: {
            disabled,
            displayValue: getDisplayValue(),
            onClick: disabled ? undefined : () => setOpen(true),
            size: size,
            readOnly: true,
            sx: (theme) => ({
              cursor: "pointer",
              "& *::selection": {
                background: "transparent !important",
                color: (theme.vars || theme).palette.text.primary,
              },

              "& .MuiOutlinedInput-root": {
                borderRadius: "var(--radius-control)",
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
              "& .MuiInputBase-input": {
                textAlign: inputAlign,
              },

              borderRadius: "var(--radius-control)",
              border: `1px solid ${(theme.vars || theme)?.palette?.primary?.main}`,
            }),
          },
          ...customSlotProps,
        }}
        showDaysOutsideCurrentMonth
        disableHighlightToday
        disablePast
        shouldDisableDate={(date) => {
          if (enabledWeeksSet.size > 0) {
            const currentDay = moment(date).startOf("day");
            const sundayOfthatWeek = currentDay
              .clone()
              .startOf("week")
              .format("YYYY-MM-DD");
            const isDisabled = !enabledWeeksSet.has(sundayOfthatWeek);

            return isDisabled;
          }
          return false;
        }}
        {...otherProps}
      />
    </LocalizationProvider>
  );
}
